import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import {
  feesApi,
  type FeeSchedule,
  type FeeType,
  type FeePreviewResult,
  type FeeDiffRow,
} from '../../api/fees.api.js';
import { feeRestoreApi } from '../../api/settings-restore.api.js';
import { APP_LOCALE } from '../../utils/locale.js';
import { RestoreReasonDialog } from '../settings/SettingsRestore.js';
import { SettingsSectionHeader } from '../settings/SettingsSectionHeader.js';
import { useCanWriteSection, useSectionRestore } from '../settings/useSettingsAccess.js';

import { describeFeeType, FeeTypeBuilder, formatCents } from './FeeTypeBuilder.js';
import { ProviderCostsSection } from './ProviderCostsSection.js';

const TEST_AMOUNTS_CENTS = ['10000', '50000', '100000', '500000'];

export function FeeConfigPage(): JSX.Element {
  // DEV-67 / DEV-140: admin and the client manager publish fee schedules; every other role reads them.
  const canEdit = useCanWriteSection({ group: 'pricing', id: 'fees' });
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [diffSchedule, setDiffSchedule] = useState<FeeSchedule | null>(null);
  const [diffVersionId, setDiffVersionId] = useState<string>('');
  const [diffRows, setDiffRows] = useState<FeeDiffRow[] | null>(null);

  // Create form state
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [feeType, setFeeType] = useState<FeeType>({ kind: 'FLAT', amountCents: '0' });
  const [effectiveFrom, setEffectiveFrom] = useState<string>(() =>
    new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
  );
  const [previewResults, setPreviewResults] = useState<FeePreviewResult[] | null>(null);

  const schedulesQuery = useQuery({
    queryKey: ['fee-schedules'],
    queryFn: () => feesApi.listSchedules(),
  });

  const grouped = useMemo(() => {
    const map = new Map<string, FeeSchedule[]>();
    for (const schedule of schedulesQuery.data ?? []) {
      const list = map.get(schedule.name) ?? [];
      list.push(schedule);
      map.set(schedule.name, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => b.version - a.version);
    }
    return Array.from(map.entries());
  }, [schedulesQuery.data]);

  // DEV-133: the newest version is the last change to customer fees.
  const lastChanged = useMemo(() => {
    const newest = [...(schedulesQuery.data ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
    return newest === undefined ? undefined : { at: newest.createdAt, by: newest.createdBy, of: 'Customer fees last' };
  }, [schedulesQuery.data]);

  const previewMutation = useMutation({
    mutationFn: () =>
      feesApi.preview({
        feeType,
        currency,
        testAmountsCents: TEST_AMOUNTS_CENTS,
      }),
    onSuccess: (results) => setPreviewResults(results),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      feesApi.createSchedule({
        name,
        currency,
        feeType,
        effectiveFrom: new Date(effectiveFrom).toISOString(),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['fee-schedules'] });
      setCreateOpen(false);
      setName('');
    },
  });

  // DEV-142: restore an earlier price, published again as a new version effective now.
  const restore = useSectionRestore({ group: 'pricing', id: 'fees' });
  const [restoreSchedule, setRestoreSchedule] = useState<FeeSchedule | null>(null);
  const [restoreNotice, setRestoreNotice] = useState<string | null>(null);
  const restoreMutation = useMutation({
    mutationFn: ({ schedule, reason }: { schedule: FeeSchedule; reason: string }) => feeRestoreApi.restore(schedule.id, reason),
    onSuccess: async (_result, { schedule }) => {
      setRestoreNotice(`${schedule.name} v${String(schedule.version)} restored as a new version, in force now.`);
      setRestoreSchedule(null);
      await queryClient.invalidateQueries({ queryKey: ['fee-schedules'] });
    },
  });

  const diffMutation = useMutation({
    mutationFn: (args: { scheduleId: string; versionId: string }) =>
      feesApi.diff(args.scheduleId, args.versionId),
    onSuccess: (result) => setDiffRows(result.rows),
  });

  return (
    <Box p={3}>
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Box flex={1}>
          <SettingsSectionHeader title="Fees and provider costs" lastChanged={lastChanged}>
            Customer fees are published as versions with an effective date; nothing is edited in place,
            so every version below stays as it was charged. Compare any version with the one before it.
          </SettingsSectionHeader>
        </Box>
        {canEdit ? <Button variant="contained" onClick={() => setCreateOpen(true)}>
          New schedule version
        </Button> : null}
      </Stack>

      {restoreNotice !== null ? <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRestoreNotice(null)}>{restoreNotice}</Alert> : null}
      <RestoreReasonDialog
        open={restoreSchedule !== null}
        title={`Restore ${restoreSchedule?.name ?? ''} v${String(restoreSchedule?.version ?? '')}`}
        description={restoreSchedule === null ? '' : `${describeFeeType(restoreSchedule.feeType)} is published again as a new version, effective now; the price in force ends now. No version is changed.`}
        saving={restoreMutation.isPending}
        {...(restoreMutation.isError ? { error: restoreMutation.error instanceof Error ? restoreMutation.error.message : 'The price could not be restored.' } : {})}
        onClose={() => { setRestoreSchedule(null); restoreMutation.reset(); }}
        onConfirm={(reason) => { if (restoreSchedule !== null) restoreMutation.mutate({ schedule: restoreSchedule, reason }); }}
      />

      {grouped.map(([groupName, versions]) => (
        <Card key={groupName} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6">{groupName}</Typography>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Version</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell>Fee</TableCell>
                  <TableCell>Effective from</TableCell>
                  <TableCell>Effective to</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.map((v, i) => {
                  const active = v.effectiveTo === undefined;
                  const previous = versions[i + 1];
                  return (
                    <TableRow key={v.id}>
                      <TableCell>
                        v{v.version}
                        {v.restoredFromVersion !== undefined
                          ? <Chip size="small" variant="outlined" color="info" sx={{ ml: 1 }} label={`Restored v${String(v.restoredFromVersion)}`} />
                          : null}
                      </TableCell>
                      <TableCell>{v.currency}</TableCell>
                      <TableCell>{describeFeeType(v.feeType)}</TableCell>
                      <TableCell>{v.effectiveFrom.slice(0, 10)}</TableCell>
                      <TableCell>{v.effectiveTo?.slice(0, 10) ?? '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={active ? 'Active' : 'Historical'}
                          color={active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(v.createdAt).toLocaleString(APP_LOCALE)} by {v.createdBy}
                      </TableCell>
                      <TableCell>
                        {previous !== undefined && (
                          <Button
                            size="small"
                            onClick={() => {
                              setDiffSchedule(v);
                              setDiffVersionId(previous.id);
                              setDiffRows(null);
                              diffMutation.mutate({
                                scheduleId: v.id,
                                versionId: previous.id,
                              });
                            }}
                          >
                            Diff vs v{previous.version}
                          </Button>
                        )}
                        {/* DEV-142: an ended price can be published again as a new version, effective now. */}
                        {restore.canRestore && v.state === 'ENDED' ? (
                          <Button size="small" color="warning" onClick={() => setRestoreSchedule(v)}>
                            Restore
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Create fee schedule version</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Schedule name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <TextField
              select
              label="Currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="GBP">GBP</MenuItem>
            </TextField>
            <FeeTypeBuilder value={feeType} onChange={setFeeType} />
            <TextField
              label="Effective from"
              type="datetime-local"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Must be now or in the future — fees are never retroactive"
            />
            <Box>
              <Button onClick={() => previewMutation.mutate()}>
                Preview fees for test amounts
              </Button>
              {previewResults !== null && (
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Amount</TableCell>
                      <TableCell>Fee</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewResults.map((r) => (
                      <TableRow key={r.amountCents}>
                        <TableCell>{formatCents(r.amountCents, currency)}</TableCell>
                        <TableCell>{formatCents(r.feeCents, currency)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={name.trim().length === 0 || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={diffSchedule !== null}
        onClose={() => setDiffSchedule(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Diff — {diffSchedule?.name} v{diffSchedule?.version} vs previous
        </DialogTitle>
        <DialogContent>
          {diffRows === null ? (
            <Typography variant="body2">Loading…</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Parameter</TableCell>
                  <TableCell>Old value</TableCell>
                  <TableCell>New value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {diffRows.map((row) => {
                  const changed = row.oldValue !== row.newValue;
                  return (
                    <TableRow
                      key={row.parameter}
                      sx={{
                        backgroundColor: changed ? 'rgba(255, 235, 59, 0.15)' : undefined,
                      }}
                    >
                      <TableCell>{row.parameter}</TableCell>
                      <TableCell>{row.oldValue ?? '—'}</TableCell>
                      <TableCell>{row.newValue ?? '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiffSchedule(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ProviderCostsSection />

      {/* unused marker to silence no-unused-vars on diffVersionId */}
      <Box sx={{ display: 'none' }}>{diffVersionId}</Box>
    </Box>
  );
}
